import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import "../../Assets/Scss/Dashboard.scss";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { useState } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { TailSpin } from "react-loader-spinner";
import { CircleFill } from "react-bootstrap-icons";
function Dashboard() {
  const initialStatusVisibility = {
    applied: true,
    accepted: true,
    approved: true,
    realised: true,
    finished: true,
    completed: true,
    rejected: true,
    dismissed: true,
  };

  const [visibleStatuses, setVisibleStatuses] = useState(
    initialStatusVisibility
  );

  const parseData = (type, data, startDate, endDate, step) => {
    if (!data) return [];

    // Fixed list of statuses to always include
    const fixedStatuses = [
      "applied",
      "accepted",
      "approved",
      "realised",
      "finished",
      "completed",
      "rejected",
      "dismissed",
    ];

    // Generate date keys based on the chosen step
    const start = new Date(startDate);
    const end = new Date(endDate);
    let dateArray = [];
    let keyFormatFunc;

    if (step === "day") {
      keyFormatFunc = (date) => {
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const yyyy = date.getFullYear();
        return `${yyyy}-${mm}/${dd}`;
      };
      let current = new Date(start);
      while (current <= end) {
        dateArray.push(keyFormatFunc(current));
        current.setDate(current.getDate() + 1);
      }
    } else if (step === "week") {
      const getWeekKey = (date) => {
        const d = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        );
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const year = d.getUTCFullYear();
        const yearStart = new Date(Date.UTC(year, 0, 1));
        const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
        return `${year}-W${String(weekNo).padStart(2, "0")}`;
      };
      keyFormatFunc = getWeekKey;
      let current = new Date(start);
      const weekSet = new Set();
      while (current <= end) {
        weekSet.add(keyFormatFunc(current));
        current.setDate(current.getDate() + 1);
      }
      dateArray = Array.from(weekSet);
      dateArray.sort();
    } else if (step === "month") {
      keyFormatFunc = (date) => {
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const yyyy = date.getFullYear();
        return `${yyyy}-${mm}`;
      };
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      while (current <= endMonth) {
        dateArray.push(keyFormatFunc(current));
        current.setMonth(current.getMonth() + 1);
      }
    }

    // Flatten the backend data.
    // For each entry, we combine the year with the key to get a full key like "2025-02/01"
    const dataMap = data.reduce((acc, yearData) => {
      const year = Object.keys(yearData)[0];
      const innerData = yearData[year];
      for (const key in innerData) {
        const fullKey = `${year}-${key}`;
        acc[fullKey] = innerData[key]; // Each value is an object of statuses
      }
      return acc;
    }, {});

    // Map over the generated date keys and build the output objects.
    return dateArray.map((key) => {
      // Start with the date name and fixed statuses initialized to 0
      const record = { name: key };
      fixedStatuses.forEach((status) => {
        record[status] = 0;
      });
      // If the dataMap has this key, override with actual values.
      if (dataMap[key]) {
        fixedStatuses.forEach((status) => {
          if (dataMap[key][status] !== undefined) {
            record[status] = dataMap[key][status];
          }
        });
      }
      return record;
    });
  };

  const determineStep = (firstDate, secondDate) => {
    // Convert the input date strings to Date objects if needed
    const start = new Date(firstDate);
    const end = new Date(secondDate);

    // Calculate the absolute difference in days
    const interval = Math.abs((end - start) / (1000 * 60 * 60 * 24));

    if (interval <= 30) {
      return "day";
    } else if (interval <= 180) {
      return "week";
    } else {
      return "month";
    }
  };

  const getTicks = (data) => {
    const maxApplicants = data.reduce(
      (max, item) => Math.max(max, item.Applicants),
      0
    );
    const upperLimit = maxApplicants + 2;
    return Array.from({ length: upperLimit }, (_, i) => i);
  };

  const api = process.env.REACT_APP_DATABASE_URI;
  const [authcode, ,] = useSessionStorage("authToken", "");
  const [appFilter, setAppFilter] = useState({
    first_date: dayjs().subtract(30, "days").format("YYYY-MM-DD"),
    second_date: dayjs().format("YYYY-MM-DD"),
    status: "applied",
  });
  const [oppFilter, setOppFilter] = useState({
    first_date: dayjs().subtract(30, "days").format("YYYY-MM-DD"),
    second_date: dayjs().format("YYYY-MM-DD"),
  });

  const {
    isPending: isPendingApp,
    data: appAnalytics,
    error,
  } = useQuery({
    queryKey: ["app-analytics", appFilter],
    retry: false,
    queryFn: () =>
      fetch(
        `${api}/analytics/applications?first_date=${appFilter.first_date}&second_date=${appFilter.second_date}&status=${appFilter.status}`,
        {
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authcode}`,
          },
        }
      ).then((res) => {
        if (!res.ok) {
          throw new Error("Network response was not ok ", error);
        }
        return res.json();
      }),
  });

  const {
    isPending: isPendingOpp,
    data: oppAnalytics,
    oppError,
  } = useQuery({
    queryKey: ["opp-analytics", oppFilter],
    retry: false,
    queryFn: () =>
      fetch(
        `${api}/analytics/opportunities?first_date=${oppFilter.first_date}&second_date=${oppFilter.second_date}`,
        {
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authcode}`,
          },
        }
      ).then((res) => {
        if (!res.ok) {
          throw new Error("Network response was not ok ", error);
        }
        return res.json();
      }),
  });

  const parsedData = parseData(
    "app",
    appAnalytics,
    appFilter.first_date,
    appFilter.second_date,
    determineStep(appFilter.first_date, appFilter.second_date)
  );

  // Extract all statuses (keys except 'name') from the first record.
  const statuses =
    parsedData.length > 0
      ? Object.keys(parsedData[0]).filter((key) => key !== "name")
      : [];

  // Define a color mapping for each status.
  const statusColors = {
    applied: "red",
    accepted: "blue",
    approved: "purple",
    realised: "teal",
    finished: "green",
    completed: "orange",
    rejected: "brown",
    dismissed: "grey",
  };

  const statusTotals = Object.keys(initialStatusVisibility).reduce(
    (acc, status) => {
      acc[status] = parsedData.reduce(
        (sum, item) => sum + (item[status] || 0),
        0
      );
      return acc;
    },
    {}
  );

  return (
    <div className="dashboard">
      <Row className="filter">
        <Col className="title">
          <p>Dashboard</p>
        </Col>
      </Row>
      <div className="chartBlock">
        <div className="titleSection">
          <p className="chartTitle">MXP Funnel</p>
          <p className="chartSubTitle">Based on Applicants for opportunities</p>
          <Form>
            <Row>
              <Col>
                <InputGroup>
                  <Form.Label id="start-date-app">
                    Choose the start date
                  </Form.Label>
                  <DatePicker
                    value={appFilter.first_date}
                    onChange={(date) => {
                      setAppFilter((prev) => ({
                        ...prev,
                        first_date: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    }}
                  />
                </InputGroup>
              </Col>
              <Col>
                <InputGroup>
                  <Form.Label id="start-date-app">
                    Choose the end date
                  </Form.Label>
                  <DatePicker
                    value={appFilter.second_date}
                    onChange={(date) => {
                      setAppFilter((prev) => ({
                        ...prev,
                        second_date: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    }}
                  />
                </InputGroup>
              </Col>
              <Col xs={12} className="statusToggles">
                <div
                  className="status-toggle-buttons"
                  style={{ marginBottom: "1rem", marginTop: "1rem" }}
                >
                  {Object.keys(initialStatusVisibility).map((status) => (
                    <Button
                      key={status}
                      variant={
                        visibleStatuses[status]
                          ? "outline-primary"
                          : "outline-secondary"
                      }
                      onClick={() =>
                        setVisibleStatuses((prev) => ({
                          ...prev,
                          [status]: !prev[status],
                        }))
                      }
                      className="status-toggle-button"
                    >
                      <div className="status-icon-wrapper">
                        <CircleFill color={statusColors[status]} size={24} />
                      </div>
                      {status}{" "}
                      <span className="status-total">
                        ({statusTotals[status] || 0})
                      </span>
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="chartSection" style={{ width: "100%", height: "40vh" }}>
          {isPendingApp ? (
            <TailSpin
              visible={true}
              height="150"
              width="150"
              color="#037EF2"
              ariaLabel="tail-spin-loading"
              radius="1"
              wrapperClass="spinner"
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={parsedData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, (dataMax) => dataMax + 1]}
                  ticks={getTicks(parsedData)}
                />
                <Tooltip />
                <Legend />
                {Object.keys(initialStatusVisibility)
                  .filter((status) => visibleStatuses[status])
                  .map((status) => (
                    <Line
                      key={status}
                      type="linear"
                      dataKey={status}
                      stroke={statusColors[status] || "#000"} // use your predefined color mapping
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="chartBlock">
        <div className="titleSection">
          <p className="chartTitle">MXP Funnel</p>
          <p className="chartSubTitle">Based on Opportunity statistics</p>
          <Form>
            <Row>
              <Col>
                <InputGroup>
                  <Form.Label id="start-date-app">
                    Choose the start date
                  </Form.Label>
                  <DatePicker
                    value={oppFilter.first_date}
                    onChange={(date) => {
                      setOppFilter((prev) => ({
                        ...prev,
                        first_date: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    }}
                  />
                </InputGroup>
              </Col>
              <Col>
                <InputGroup>
                  <Form.Label id="start-date-app">
                    Choose the end date
                  </Form.Label>
                  <DatePicker
                    value={oppFilter.second_date}
                    onChange={(date) => {
                      setOppFilter((prev) => ({
                        ...prev,
                        second_date: dayjs(date).format("YYYY-MM-DD"),
                      }));
                    }}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="chartSection" style={{ width: "100%", height: "40vh" }}>
          {isPendingApp ? (
            <TailSpin
              visible={true}
              height="150"
              width="150"
              color="#037EF2"
              ariaLabel="tail-spin-loading"
              radius="1"
              wrapperClass="spinner"
            />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={parseData(
                  "opp",
                  oppAnalytics,
                  oppFilter.first_date,
                  oppFilter.second_date,
                  determineStep(oppFilter.first_date, oppFilter.second_date)
                )}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="name" />
                <YAxis
                  domain={[0, (dataMax) => dataMax + 0.01]}
                  ticks={getTicks(
                    parseData(
                      "opp",
                      oppAnalytics,
                      oppFilter.first_date,
                      oppFilter.second_date,
                      determineStep(oppFilter.first_date, oppFilter.second_date)
                    )
                  )}
                />
                <Tooltip />
                <Legend />
                <Line type="linear" dataKey="Opportunities" stroke="red" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
export default Dashboard;
